// fe-ios12/src/context/NotificationContext.js
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from '../components/api';
import { useSessionManager } from './SessionManagerContext';
import AuthContext from './AuthProvider';
import { useToast } from './ToastContext';

const NotificationContext = createContext(null);

const PENDING_DELAY_MS = 20 * 60 * 1000; // 20 minutes

// Map notification type/subType to toast type
function toastTypeFor(notif) {
  if (notif.type === 'ESCALATION')              return 'error';
  if (notif.type === 'PLAN_APPROVAL')           return 'info';
  if (notif.subType === 'plan_approved')        return 'success';
  if (notif.subType === 'plan_rejected')        return 'error';
  if (notif.subType === 'appointment_reminder') return 'reminder';
  if (notif.subType === 'renewal_reminder')     return 'renewal';
  if (notif.subType === 'inactive_patient')     return 'inactive';
  if (notif.subType === 'payment_pending')      return 'payment';
  if (notif.subType === 'session_pending_escalated') return 'warning';
  if (notif.subType === 'delayed_task')         return 'task';
  if (notif.type === 'SESSION_PENDING')         return 'warning';
  if (notif.type === 'KYC_UPDATE')              return 'info';
  if (notif.type === 'PASSWORD_CHANGED')        return 'warning';
  if (notif.type === 'CHECKLIST_MISSED')        return 'warning';
  if (notif.type === 'TASK_REMINDER')           return 'info';
  return 'info';
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications]     = useState([]);   // active (not resolved, not expired)
  const [sseReady, setSseReady]               = useState(false);
  const sseRef            = useRef(null);
  const scheduledRef      = useRef({});  // patientCard → timeoutId (so we don't double-schedule)
  const { sessions }      = useSessionManager() || { sessions: {} };
  const { addToast }      = useToast() || {};

  // ── Toast throttle — max 3 toasts per 30s window, group by type ────────
  const toastWindowRef    = useRef([]);   // timestamps of recent toasts
  const suppressedByType  = useRef({});   // { type: count }
  const summaryTimerRef   = useRef(null);
  const TOAST_WINDOW_MS   = 30000;        // 30 seconds
  const MAX_TOASTS        = 3;

  // Urgent types get individual toasts; non-urgent get grouped
  const URGENT_TYPES = new Set(['error', 'warning']);

  const throttledToast = useCallback((title, message, type, notifId) => {
    if (!addToast) return;
    const onClick = notifId ? () => { window.location.href = `/Notifications`; } : undefined;
    const now = Date.now();
    toastWindowRef.current = toastWindowRef.current.filter(t => now - t < TOAST_WINDOW_MS);

    const isUrgent = URGENT_TYPES.has(type);

    if (isUrgent && toastWindowRef.current.length < MAX_TOASTS) {
      // Urgent: show immediately if under limit
      toastWindowRef.current.push(now);
      addToast(title, message, type, 3000, onClick);
    } else if (!isUrgent && toastWindowRef.current.length < 1) {
      // Non-urgent: show at most 1 immediately
      toastWindowRef.current.push(now);
      addToast(title, message, type, 3000, onClick);
    } else {
      // Suppress and group by type
      const label = type || 'info';
      suppressedByType.current[label] = (suppressedByType.current[label] || 0) + 1;
      clearTimeout(summaryTimerRef.current);
      summaryTimerRef.current = setTimeout(() => {
        const groups = suppressedByType.current;
        const total = Object.values(groups).reduce((a, b) => a + b, 0);
        if (total > 0) {
          const parts = Object.entries(groups).map(([t, c]) => `${c} ${t}`);
          addToast('Notifications', parts.join(', '), 'info');
          suppressedByType.current = {};
        }
      }, TOAST_WINDOW_MS);
    }
  }, [addToast]);

  // Auth context — subscribed only for re-renders when user logs in.
  // Use sessionStorage (set on active login) NOT auth.token (localStorage) to avoid
  // triggering API calls with stale cross-session tokens that api.js won't actually send.
  const { auth } = useContext(AuthContext); // eslint-disable-line no-unused-vars
  const authToken    = sessionStorage.getItem('token');
  const authUsername = sessionStorage.getItem('lu');

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const sessionPendingNotifs = notifications.filter(
    n => n.type === 'SESSION_PENDING' && !n.resolved
  );
  const unreadCount = notifications.filter(n => !n.read && !n.resolved).length;

  // ── Fetch all active notifications (REST fallback / initial load) ────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch (e) {
      // silently ignore — SSE will keep state in sync
    }
  }, []);

  // ── SSE connection — re-runs when the user logs in (authToken changes) ────────
  useEffect(() => {
    const token    = authToken;
    const username = authUsername;
    if (!token || !username) return;

    // Initial REST fetch
    fetchNotifications();

    const baseUrl = process.env.REACT_APP_URL || '';
    const sseUrl  = `${baseUrl}/notifications/stream?token=${encodeURIComponent(token)}&u=${encodeURIComponent(username)}`;

    const es = new EventSource(sseUrl);
    sseRef.current = es;

    es.addEventListener('connected', () => setSseReady(true));

    es.addEventListener('notifications', e => {
      try {
        const data = JSON.parse(e.data);
        setNotifications(data);
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('new_notification', e => {
      try {
        const notif = JSON.parse(e.data);
        setNotifications(prev => {
          const exists = prev.some(n => n._id === notif._id);
          return exists ? prev : [notif, ...prev];
        });
        // Throttled toast — prevents flood when many arrive at once
        throttledToast(notif.title, notif.message, toastTypeFor(notif), notif._id);
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('notification_updated', e => {
      try {
        const notif = JSON.parse(e.data);
        setNotifications(prev =>
          prev.map(n => n._id === notif._id ? notif : n)
               .filter(n => !n.resolved)
        );
        if (notif.resolved) {
          throttledToast('Notification resolved', notif.title, 'success');
        }
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('auto_task_created', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast('Task Created', data.message || 'A new task was assigned to you.', 'task');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('escalation_reminder', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Escalation Reminder', data.message || 'Pending escalation needs attention.', 'error');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('escalation_sent', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast('Escalation Sent', data.message || 'Escalation has been sent.', 'success');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('escalation_acknowledged', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast('Escalation Acknowledged', data.message || 'Your escalation was acknowledged.', 'success');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('appointment_reminder', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Appointment Reminder', data.message || 'You have sessions scheduled.', 'reminder');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('renewal_reminder', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Renewal Needed', data.message || 'A saadhak needs session renewal.', 'renewal');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('inactive_patient', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Inactive Saadhak', data.message || 'A saadhak has not visited recently.', 'inactive');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('payment_pending', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Payment Pending', data.message || 'A session payment is awaiting confirmation.', 'payment');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('session_auto_marked', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast('Session Auto-Completed', data.message || 'A session was automatically marked done at 9 PM.', 'info');
      } catch (e) { /* ignore */ }
    });

    es.addEventListener('delayed_task_alert', e => {
      try {
        const data = JSON.parse(e.data);
        throttledToast(data.title || 'Task Overdue', data.message || 'A task is overdue and needs attention.', 'task');
      } catch (e) { /* ignore */ }
    });

    es.onerror = () => {
      // EventSource auto-reconnects; no action needed
    };

    return () => {
      es.close();
      sseRef.current = null;
      setSseReady(false);
    };
  }, [authToken, authUsername]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Web Push subscription — request permission and subscribe ─────────────────
  useEffect(() => {
    const token    = authToken;
    const username = authUsername;
    if (!token || !username) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    async function subscribePush() {
      try {
        // Skip if permission already granted or permanently denied — avoids re-prompting on every load
        if (typeof Notification !== 'undefined') {
          if (Notification.permission === 'denied') return;           // user blocked — don't nag
          if (Notification.permission !== 'granted') {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') return;
          }
        } else return;

        const reg = await navigator.serviceWorker.ready;

        // Get VAPID public key from backend
        const keyRes = await api.get('/push/vapid-public');
        const vapidPublicKey = keyRes.data.publicKey;

        // Convert URL-safe base64 to Uint8Array
        const padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
        const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const applicationServerKey = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; i++) applicationServerKey[i] = rawData.charCodeAt(i);

        // Always unsubscribe and re-subscribe to ensure a fresh endpoint
        // (fixes cases where Chrome shortcut was removed and PWA reinstalled)
        let subscription = await reg.pushManager.getSubscription();
        if (subscription) {
          try { await subscription.unsubscribe(); } catch (_) {}
        }
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });

        // Send subscription to backend
        await api.post('/push/subscribe', { username, subscription: subscription.toJSON() });
      } catch (err) {
        console.warn('Push subscription failed:', err.message);
      }
    }

    subscribePush();
  }, [authToken, authUsername]);

  // ── Listen for SW push navigation messages ──────────────────────────────────
  useEffect(() => {
    function onSwMessage(event) {
      if (event.data && event.data.type === 'PUSH_NAV' && event.data.url) {
        window.location.href = event.data.url;
      }
    }
    navigator.serviceWorker && navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => {
      navigator.serviceWorker && navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, []);

  // ── Watch sessions for finishedAt — schedule 20-min pending check ─────────────
  useEffect(() => {
    Object.values(sessions).forEach(sess => {
      const { patientCard, finishedAt, appointment } = sess;
      if (!finishedAt || !appointment?._id || scheduledRef.current[patientCard]) return;

      const delay = Math.max(0, finishedAt + PENDING_DELAY_MS - Date.now());

      const tid = setTimeout(async () => {
        delete scheduledRef.current[patientCard];
        try {
          // Check live appointment state
          const apptRes = await api.get(`/appointments/${appointment._id}`);
          const appt    = apptRes.data;

          const pendingFields = [];
          if (appt.status !== 'session completed')       pendingFields.push('mark_over');
          if (!appt.sessionComments?.trim())             pendingFields.push('sessionComments');
          if (!appt.sessionComments?.trim())             pendingFields.push('recommendations');
          const fb = appt.sessionFeedback;
          if (!fb || Object.keys(fb).length === 0)       pendingFields.push('readings');

          if (pendingFields.length === 0) return; // nothing pending — skip

          const username = sessionStorage.getItem('lu');
          const fieldLabels = {
            mark_over:       'Mark session over',
            sessionComments: 'Session comments',
            recommendations: 'Recommendations',
            readings:        'ROM / body readings',
          };
          const labelList = pendingFields.map(f => fieldLabels[f] || f).join(', ');

          await api.post('/notifications', {
            userId:        username,
            title:         `Session inputs pending — ${sess.patientName || patientCard}`,
            message:       `The following inputs are still pending for this session: ${labelList}.`,
            type:          'SESSION_PENDING',
            subType:       pendingFields.length === 1 ? pendingFields[0] : 'mixed',
            appointmentId: appointment._id,
            patientCard,
            patientName:   sess.patientName || '',
            pendingFields,
          });
          // SSE will deliver new_notification → state updates automatically
        } catch (e) {
          // silently ignore
        }
      }, delay);

      scheduledRef.current[patientCard] = tid;
    });
  }, [sessions]);

  // ── Acknowledge a notification ────────────────────────────────────────────────
  const acknowledgeNotification = useCallback(async (id) => {
    try {
      const res = await api.patch(`/notifications/${id}/ack`);
      const { notif } = res.data;
      setNotifications(prev =>
        prev.map(n => n._id === id ? notif : n)
             .filter(n => !n.resolved)
      );
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  // ── Mark a notification as read ───────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    try {
      const res = await api.patch(`/notifications/${id}/read`);
      const notif = res.data;
      setNotifications(prev => prev.map(n => n._id === id ? notif : n));
    } catch (e) { /* ignore */ }
  }, []);

  // ── Clear scheduled timers on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(scheduledRef.current).forEach(clearTimeout);
    };
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      sessionPendingNotifs,
      unreadCount,
      sseReady,
      fetchNotifications,
      acknowledgeNotification,
      markRead,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
